/**
 * ZAPHUB 2.1 — AICopilotPanel
 *
 * Full AI Copilot interface with:
 *   - Multi-turn chat (claude-sonnet-4-6)
 *   - Natural language workflow generation
 *   - Workflow optimization with real AI analysis
 *   - Self-healing for failed nodes
 *   - ROI estimation
 *
 * All data comes from /api/ai-copilot — no mocks.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  Sparkles,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Shield,
  Zap,
  DollarSign,
  X,
  Send,
  RefreshCw,
  Wand2,
  Bot,
  User,
  Copy,
  Download,
  Euro,
  Clock,
  Activity,
  Loader2,
} from 'lucide-react';
import api from '../services/apiClient';
import type { Workflow } from '@shared/types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  updatedWorkflow?: any;
}

interface OptimizationResult {
  optimizedWorkflow: any;
  changes: Array<{ type: 'added' | 'removed' | 'modified'; node: string; reason: string }>;
  estimatedImprovementPercent: number;
  reasoning: string;
}

interface ROIEstimate {
  hoursPerMonth: number;
  costPerMonth: number;
  annualSavings: number;
  paybackPeriodDays: number;
  breakdown: Array<{ task: string; minutesSaved: number }>;
}

interface GeneratedWorkflow {
  name: string;
  description: string;
  trigger: string;
  nodes: any[];
  tags: string[];
  complexity: string;
  requiresApproval: boolean;
}

interface GenerateResult {
  workflow: GeneratedWorkflow;
  confidence: number;
  reasoning: string;
  suggestions: string[];
  warnings: string[];
  tokensUsed: number;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface AICopilotPanelProps {
  /** Pass the full workflow object OR just the workflowId — both are accepted */
  workflow?: Workflow;
  workflowId?: string;
  onApplySuggestion?: (optimizedWorkflow: any) => void;
  onWorkflowGenerated?: (workflow: GeneratedWorkflow) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function genSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

const ThinkingDots: React.FC = () => (
  <div className="flex items-center gap-1 py-1">
    <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0ms' }} />
    <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '150ms' }} />
    <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '300ms' }} />
  </div>
);

const MessageBubble: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? 'bg-blue-500' : 'bg-gradient-to-br from-purple-500 to-pink-500'
      }`}>
        {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
      </div>
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-blue-500 text-white rounded-tr-sm'
            : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'
        }`}>
          {msg.content}
          {msg.updatedWorkflow && (
            <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded-lg text-purple-800 text-xs">
              Workflow updated: <strong>{msg.updatedWorkflow.name}</strong>
              {' '}({msg.updatedWorkflow.nodes?.length || 0} nodes)
            </div>
          )}
        </div>
        <span className="text-xs text-gray-400 px-1">
          {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const AICopilotPanel: React.FC<AICopilotPanelProps> = ({
  workflow: workflowProp,
  workflowId,
  onApplySuggestion,
  onWorkflowGenerated,
}) => {
  // Resolve workflow — use prop directly or fetch by id
  const [fetchedWorkflow, setFetchedWorkflow] = useState<Workflow | undefined>(undefined);
  const workflow = workflowProp || fetchedWorkflow;

  useEffect(() => {
    if (!workflowProp && workflowId) {
      api.get(`/workflows/${workflowId}`)
        .then(({ data }) => setFetchedWorkflow(data.data || data))
        .catch(() => {}); // silently ignore — Copilot still works without workflow context
    }
  }, [workflowId, workflowProp]);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: workflow
        ? `Hola! Soy el Copilot de ZAPHUB, potenciado por Claude.\n\nEstoy listo para ayudarte con "${workflow.name}". Puedo explicarlo, optimizarlo, corregir errores, o simplemente responder tus preguntas sobre automatización.\n\n¿En qué empezamos?`
        : `Hola! Soy el Copilot de ZAPHUB, potenciado por Claude.\n\nPuedo generar workflows desde lenguaje natural, optimizar los existentes, calcular ROI, y mucho más.\n\nDescríbeme qué quieres automatizar y lo construimos juntos.`,
      timestamp: new Date(),
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(genSessionId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Generate state
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateResult, setGenerateResult] = useState<GenerateResult | null>(null);
  const [generateError, setGenerateError] = useState('');
  const [savingGenerated, setSavingGenerated] = useState(false);

  // Optimize state
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState<OptimizationResult | null>(null);
  const [optimizeGoals, setOptimizeGoals] = useState('');
  const [optimizeError, setOptimizeError] = useState('');

  // ROI state
  const [roiLoading, setRoiLoading] = useState(false);
  const [roiResult, setRoiResult] = useState<ROIEstimate | null>(null);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [roiError, setRoiError] = useState('');

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  // ── Chat ───────────────────────────────────────────────────────────────────

  const sendMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;

    setChatInput('');
    const userMsg: ChatMessage = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setChatLoading(true);

    try {
      const { data } = await api.post('/ai-copilot/chat', {
        sessionId,
        message: text,
        workflowContext: workflow || null,
      });

      const result = data.data;
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: result.reply,
        timestamp: new Date(),
        updatedWorkflow: result.updatedWorkflow,
      };
      setMessages(prev => [...prev, assistantMsg]);

      if (result.updatedWorkflow && onApplySuggestion) {
        onApplySuggestion(result.updatedWorkflow);
      }
    } catch (err: any) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `Error al conectar con Copilot: ${err?.response?.data?.error || err.message}. Asegúrate de que ANTHROPIC_API_KEY está configurada en el servidor.`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, sessionId, workflow, onApplySuggestion]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Generate ───────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!generatePrompt.trim()) return;
    setGenerateLoading(true);
    setGenerateError('');
    setGenerateResult(null);

    try {
      const { data } = await api.post('/ai-copilot/generate', {
        prompt: generatePrompt.trim(),
        language: 'es',
      });
      setGenerateResult(data.data);
    } catch (err: any) {
      setGenerateError(err?.response?.data?.error || err.message);
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSaveGenerated = async () => {
    if (!generateResult) return;
    setSavingGenerated(true);
    try {
      const { data } = await api.post('/ai-copilot/save-generated', {
        workflow: generateResult.workflow,
      });
      if (onWorkflowGenerated) onWorkflowGenerated(generateResult.workflow);
      alert(`Workflow "${data.data.name}" guardado correctamente.`);
    } catch (err: any) {
      alert(`Error al guardar: ${err?.response?.data?.error || err.message}`);
    } finally {
      setSavingGenerated(false);
    }
  };

  // ── Optimize ───────────────────────────────────────────────────────────────

  const handleOptimize = async () => {
    if (!workflow?.id) return;
    setOptimizeLoading(true);
    setOptimizeError('');
    setOptimizeResult(null);

    try {
      const { data } = await api.post(`/ai-copilot/optimize/${workflow.id}`, {
        goals: optimizeGoals || undefined,
      });
      setOptimizeResult(data.data);
    } catch (err: any) {
      setOptimizeError(err?.response?.data?.error || err.message);
    } finally {
      setOptimizeLoading(false);
    }
  };

  const handleApplyOptimization = () => {
    if (optimizeResult && onApplySuggestion) {
      onApplySuggestion(optimizeResult.optimizedWorkflow);
    }
  };

  // ── ROI ────────────────────────────────────────────────────────────────────

  const handleROI = async () => {
    if (!workflow?.id) return;
    setRoiLoading(true);
    setRoiError('');
    setRoiResult(null);

    try {
      const { data } = await api.post(`/ai-copilot/roi/${workflow.id}`, { hourlyRate });
      setRoiResult(data.data);
    } catch (err: any) {
      setRoiError(err?.response?.data?.error || err.message);
    } finally {
      setRoiLoading(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900">ZAPHUB Copilot</h2>
          <p className="text-xs text-gray-500">claude-sonnet-4-6 · enterprise AI</p>
        </div>
        {workflow && (
          <Badge variant="secondary" className="ml-auto text-xs">
            {workflow.name}
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="chat" className="flex flex-col flex-1 overflow-hidden">
        <TabsList className="mx-4 mt-3 grid grid-cols-4">
          <TabsTrigger value="chat" className="text-xs">
            <Bot className="w-3.5 h-3.5 mr-1" /> Chat
          </TabsTrigger>
          <TabsTrigger value="generate" className="text-xs">
            <Wand2 className="w-3.5 h-3.5 mr-1" /> Generar
          </TabsTrigger>
          {workflow && (
            <TabsTrigger value="optimize" className="text-xs">
              <Zap className="w-3.5 h-3.5 mr-1" /> Optimizar
            </TabsTrigger>
          )}
          {workflow && (
            <TabsTrigger value="roi" className="text-xs">
              <Euro className="w-3.5 h-3.5 mr-1" /> ROI
            </TabsTrigger>
          )}
          {!workflow && <TabsTrigger value="optimize" disabled className="text-xs opacity-40">Optimizar</TabsTrigger>}
          {!workflow && <TabsTrigger value="roi" disabled className="text-xs opacity-40">ROI</TabsTrigger>}
        </TabsList>

        {/* ── Chat Tab ── */}
        <TabsContent value="chat" className="flex flex-col flex-1 overflow-hidden m-0 px-4">
          <ScrollArea className="flex-1 py-4">
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}
            {chatLoading && (
              <div className="flex gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <ThinkingDots />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Input */}
          <div className="pb-4 pt-2 border-t">
            <div className="flex gap-2">
              <Textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pregunta sobre el workflow, pide mejoras, describe errores..."
                className="min-h-[60px] max-h-[120px] resize-none text-sm"
                disabled={chatLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!chatInput.trim() || chatLoading}
                size="icon"
                className="self-end h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Enter para enviar · Shift+Enter nueva línea</p>
          </div>
        </TabsContent>

        {/* ── Generate Tab ── */}
        <TabsContent value="generate" className="flex-1 overflow-auto px-4 py-4 space-y-4">
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">Genera desde lenguaje natural</h3>
            </div>
            <Textarea
              value={generatePrompt}
              onChange={e => setGeneratePrompt(e.target.value)}
              placeholder="Describe el proceso que quieres automatizar. Ejemplo: Cada lunes a las 9am, revisa los empleados con más de 5 aprobaciones y envíales un bono de €500 con email de notificación al manager..."
              className="min-h-[120px] text-sm bg-white"
              disabled={generateLoading}
            />
            <Button
              onClick={handleGenerate}
              disabled={!generatePrompt.trim() || generateLoading}
              className="mt-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {generateLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generar Workflow</>
              )}
            </Button>
          </Card>

          {generateError && (
            <Card className="p-4 bg-red-50 border-red-200">
              <div className="flex gap-2 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{generateError}</p>
              </div>
            </Card>
          )}

          {generateResult && (
            <Card className="p-4 border-purple-200">
              {/* Summary */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <h3 className="font-semibold">{generateResult.workflow.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{generateResult.workflow.description}</p>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>{Math.round(generateResult.confidence * 100)}% confianza</div>
                  <div>{generateResult.tokensUsed} tokens</div>
                </div>
              </div>

              {/* Metadata */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline">Trigger: {generateResult.workflow.trigger}</Badge>
                <Badge variant="outline">{generateResult.workflow.nodes.length} nodos</Badge>
                <Badge variant={generateResult.workflow.complexity === 'complex' ? 'destructive' : 'secondary'}>
                  {generateResult.workflow.complexity}
                </Badge>
                {generateResult.workflow.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>

              {/* Nodes preview */}
              <div className="space-y-2 mb-4">
                <h4 className="text-sm font-medium text-gray-700">Nodos generados:</h4>
                {generateResult.workflow.nodes.map((node: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs text-purple-700 font-medium">
                      {i + 1}
                    </div>
                    <div>
                      <span className="text-sm font-medium">{node.label}</span>
                      <span className="text-xs text-gray-500 ml-2">({node.type})</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reasoning */}
              <details className="mb-4">
                <summary className="text-sm text-purple-600 cursor-pointer font-medium">
                  Ver razonamiento de Claude
                </summary>
                <p className="text-sm text-gray-700 mt-2 bg-purple-50 p-3 rounded-lg">
                  {generateResult.reasoning}
                </p>
              </details>

              {/* Warnings */}
              {generateResult.warnings.length > 0 && (
                <div className="mb-4 space-y-1">
                  {generateResult.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-amber-700 text-sm">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveGenerated}
                  disabled={savingGenerated}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingGenerated ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Guardar en ZAPHUB
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(JSON.stringify(generateResult.workflow, null, 2))}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copiar JSON
                </Button>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ── Optimize Tab ── */}
        <TabsContent value="optimize" className="flex-1 overflow-auto px-4 py-4 space-y-4">
          {!workflow ? (
            <Card className="p-8 text-center text-gray-500">
              <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Abre un workflow para poder optimizarlo</p>
            </Card>
          ) : (
            <>
              <Card className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  <h3 className="font-semibold text-yellow-900">Optimización con IA</h3>
                </div>
                <Textarea
                  value={optimizeGoals}
                  onChange={e => setOptimizeGoals(e.target.value)}
                  placeholder="Objetivos de optimización (opcional). Ej: reducir latencia, menor coste de API, mayor fiabilidad..."
                  className="min-h-[80px] text-sm bg-white"
                  disabled={optimizeLoading}
                />
                <Button
                  onClick={handleOptimize}
                  disabled={optimizeLoading}
                  className="mt-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                >
                  {optimizeLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Optimizar con Claude</>
                  )}
                </Button>
              </Card>

              {optimizeError && (
                <Card className="p-4 bg-red-50 border-red-200">
                  <div className="flex gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{optimizeError}</p>
                  </div>
                </Card>
              )}

              {optimizeResult && (
                <Card className="p-4">
                  {/* Improvement */}
                  <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 rounded-lg">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                    <div>
                      <div className="text-2xl font-bold text-green-700">
                        +{optimizeResult.estimatedImprovementPercent}%
                      </div>
                      <div className="text-xs text-green-600">mejora estimada</div>
                    </div>
                  </div>

                  {/* Changes */}
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Cambios propuestos:</h4>
                  <div className="space-y-2 mb-4">
                    {optimizeResult.changes.map((change, i) => (
                      <div key={i} className={`flex items-start gap-3 p-2 rounded-lg text-sm ${
                        change.type === 'added' ? 'bg-green-50 text-green-800' :
                        change.type === 'removed' ? 'bg-red-50 text-red-800' :
                        'bg-blue-50 text-blue-800'
                      }`}>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {change.type}
                        </Badge>
                        <div>
                          <span className="font-medium">{change.node}</span>
                          <span className="mx-1">—</span>
                          <span>{change.reason}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Reasoning */}
                  <details className="mb-4">
                    <summary className="text-sm text-yellow-600 cursor-pointer font-medium">
                      Ver análisis completo
                    </summary>
                    <p className="text-sm text-gray-700 mt-2 bg-yellow-50 p-3 rounded-lg">
                      {optimizeResult.reasoning}
                    </p>
                  </details>

                  <Button
                    onClick={handleApplyOptimization}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 w-full"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Aplicar Optimización
                  </Button>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ── ROI Tab ── */}
        <TabsContent value="roi" className="flex-1 overflow-auto px-4 py-4 space-y-4">
          {!workflow ? (
            <Card className="p-8 text-center text-gray-500">
              <Euro className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Abre un workflow para calcular su ROI</p>
            </Card>
          ) : (
            <>
              <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">Estimación de ROI</h3>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <label className="text-sm text-gray-600 whitespace-nowrap">
                    Coste/hora (€):
                  </label>
                  <input
                    type="number"
                    min={10}
                    max={500}
                    value={hourlyRate}
                    onChange={e => setHourlyRate(Number(e.target.value))}
                    className="w-24 px-3 py-1.5 border rounded-lg text-sm"
                  />
                </div>
                <Button
                  onClick={handleROI}
                  disabled={roiLoading}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                >
                  {roiLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculando...</>
                  ) : (
                    <><Activity className="w-4 h-4 mr-2" /> Calcular ROI</>
                  )}
                </Button>
              </Card>

              {roiError && (
                <Card className="p-4 bg-red-50 border-red-200">
                  <div className="flex gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{roiError}</p>
                  </div>
                </Card>
              )}

              {roiResult && (
                <Card className="p-4">
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <Clock className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-blue-700">
                        {roiResult.hoursPerMonth}h
                      </div>
                      <div className="text-xs text-blue-600">ahorradas/mes</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <Euro className="w-6 h-6 text-green-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-green-700">
                        €{roiResult.costPerMonth.toLocaleString()}
                      </div>
                      <div className="text-xs text-green-600">ahorro/mes</div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-purple-700">
                        €{roiResult.annualSavings.toLocaleString()}
                      </div>
                      <div className="text-xs text-purple-600">ahorro anual</div>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4 text-center">
                      <Shield className="w-6 h-6 text-orange-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-orange-700">
                        {roiResult.paybackPeriodDays}d
                      </div>
                      <div className="text-xs text-orange-600">retorno inversión</div>
                    </div>
                  </div>

                  {/* Breakdown */}
                  {roiResult.breakdown?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Desglose de tareas automatizadas:</h4>
                      <div className="space-y-2">
                        {roiResult.breakdown.map((item, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                            <span className="text-gray-700">{item.task}</span>
                            <Badge variant="secondary">
                              -{item.minutesSaved}min/ejecución
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
